// `/slack/link?request=…` — where Scout's private Slack sign-in link lands (GRO-161).
//
// The person is already signed in with Clerk by the time this renders (every route is behind
// the gate), so linking is one confirmation: "this Slack account is you". The link is made for
// one Slack account and carries that account's own email; the server links it only when the
// Clerk session is that address, so a forwarded link links nobody. The page shows which Slack
// account it is (user id and workspace) and says plainly when the signed-in address is not the
// one it is for. After linking, the person mentions @scout again in Slack.

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchSlackLinkRequest, linkSlack, panelMessage } from '../api/client';
import type { SlackLinkResult } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { Gate } from '../auth/Gate';

export function SlackLinkView() {
  const { getToken } = useAuthContext();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request') || '';
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SlackLinkResult | null>(null);
  const [errorText, setErrorText] = useState('');

  const linkQuery = useQuery({
    queryKey: ['slack-link', requestId],
    queryFn: () => fetchSlackLinkRequest(getToken, requestId),
    enabled: Boolean(requestId),
    staleTime: 0,
  });

  async function confirm() {
    setBusy(true);
    setErrorText('');
    try {
      setResult(await linkSlack(getToken, requestId));
    } catch (error) {
      setErrorText(panelMessage(error, 'Could not link Slack.'));
    } finally {
      setBusy(false);
    }
  }

  if (!requestId) {
    return <Gate message="This page needs the link Scout sent you in Slack." />;
  }
  if (linkQuery.isPending) {
    return <div className="loading">Checking the link…</div>;
  }
  if (linkQuery.error) {
    return <Gate message={panelMessage(linkQuery.error, 'This link could not be read.')} />;
  }

  if (result) {
    return (
      <Gate message={`Slack is linked to ${result.email} for ${result.link_days} days.`}>
        <p className="denied-who">Go back to Slack and mention @scout again in the thread.</p>
      </Gate>
    );
  }

  const linkRequest = linkQuery.data;
  if (linkRequest.completed) {
    return <Gate message="This link was already used. Mention @scout again if it is still waiting." />;
  }
  if (linkRequest.expired) {
    return <Gate message="This link has expired. Mention @scout again for a new one." />;
  }

  const slackAccount = `Slack user ${linkRequest.slack_user_id ?? '?'} in workspace ${linkRequest.slack_team_id ?? '?'}`;

  // The link is for someone else's Slack account. Say so rather than offer a button that refuses.
  if (!linkRequest.matches) {
    return (
      <Gate message={`This link is for the Slack account of ${linkRequest.slack_email}.`}>
        <p className="denied-who">
          You are signed in as {linkRequest.email}. Only {linkRequest.slack_email} can link it. If
          someone sent you this link, you do not need it — mention @scout yourself to get your own.
        </p>
      </Gate>
    );
  }

  const slackName = linkRequest.display_name || 'your Slack account';
  return (
    <Gate message={`Link ${slackName} to ${linkRequest.email}?`}>
      <p className="denied-who">
        {slackAccount}. Scout in Slack will act as {linkRequest.email} for the next 30 days.
      </p>
      {errorText ? <div className="err">{errorText}</div> : null}
      <button className="btn" onClick={confirm} disabled={busy}>
        {busy ? 'Linking…' : 'Link Slack'}
      </button>
    </Gate>
  );
}
